import React from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { TrackableList, CreateTrackableItem } from 'types/trackables';

import { useCreateTrackableItem } from '../hooks/useCreateTrackableItem';
import { useUploadTrackableFile } from '../hooks/useUploadTrackableFile';
import { TRACKABLE_OBJECT_TYPE_LABELS } from '../utils';

import { Drawer, DrawerProps } from 'components/Drawer/DrawerDeprecated';
import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { Textarea } from 'components/Textarea/Textarea';
import { Text } from 'components/Text/Text';
import { Icon } from 'components/Icon/Icon';

export type BulkAddTrackableItemsDialogProps = DrawerProps & {
  trackableList: TrackableList;
  onSuccess?: () => void;
};

interface FaceFormData {
  description?: string;
  labels?: string;
  files: FileList | null;
}

interface NonFaceFormData {
  description?: string;
  labels?: string;
  items: string;
}

export function BulkAddTrackableItemsDialog({
  trackableList,
  open,
  onClose,
  onSuccess,
}: BulkAddTrackableItemsDialogProps) {
  const queryClient = useQueryClient();
  const createTrackableItem = useCreateTrackableItem();
  const {
    uploadMultipleTrackableFiles,
    uploadProgress,
    isLoading: isUploading,
  } = useUploadTrackableFile();

  const isFaceList = trackableList.object_type === 'face';
  const isVehicleList = trackableList.object_type === 'vehicle';
  const isCustomList = trackableList.object_type === 'custom';

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [processStatus, setProcessStatus] = React.useState<string>('');
  const [fileStatuses, setFileStatuses] = React.useState<
    Record<string, 'pending' | 'processing' | 'success' | 'error'>
  >({});

  // Form for face lists
  const faceForm = useForm<FaceFormData>({
    defaultValues: {
      description: '',
      labels: '',
      files: null,
    },
  });

  // Form for non-face lists
  const nonFaceForm = useForm<NonFaceFormData>({
    defaultValues: {
      description: '',
      labels: '',
      items: '',
    },
  });

  const form = isFaceList ? faceForm : nonFaceForm;

  const removeFile = (indexToRemove: number) => {
    // Don't allow removal during processing
    if (isProcessing) return;

    const watchedFiles = faceForm.watch('files');
    if (!watchedFiles) return;

    const dt = new DataTransfer();
    Array.from(watchedFiles).forEach((file, index) => {
      if (index !== indexToRemove) {
        dt.items.add(file);
      }
    });

    // Update the form with new FileList
    const fileInput = document.querySelector(
      'input[type="file"]'
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.files = dt.files;
      fileInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const handleFaceSubmit = async (data: FaceFormData) => {
    if (!data.files || data.files.length === 0) return;

    setIsProcessing(true);
    setProcessStatus('Preparing to create items and upload files...');

    try {
      const files = Array.from(data.files);
      const labels = data.labels
        ? data.labels
            .split(',')
            .map((l) => l.trim())
            .filter(Boolean)
        : [];

      // Initialize file statuses
      const initialStatuses: Record<
        string,
        'pending' | 'processing' | 'success' | 'error'
      > = {};
      files.forEach((file, index) => {
        initialStatuses[`${file.name}-${index}`] = 'pending';
      });
      setFileStatuses(initialStatuses);

      let created = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileKey = `${file.name}-${i}`;

        // Extract name from filename (without extension)
        const name = file.name.replace(/\.[^/.]+$/, '');

        // Update status to processing
        setFileStatuses((prev) => ({ ...prev, [fileKey]: 'processing' }));
        setProcessStatus(
          `Creating item ${created + 1} of ${files.length}: ${name}`
        );

        try {
          // Create the trackable item
          const newItem: CreateTrackableItem = {
            name,
            description: data.description,
            labels,
          };

          const createdItem = await createTrackableItem.mutateAsync({
            trackable_list_id: trackableList.id,
            ...newItem,
          });

          // Upload the file for this item
          if (createdItem) {
            await uploadMultipleTrackableFiles([file], {
              trackable_list_id: trackableList.id,
              trackable_item_id: createdItem.id,
            });
          }

          // Mark as success
          setFileStatuses((prev) => ({ ...prev, [fileKey]: 'success' }));
          created++;
        } catch (error) {
          // Mark as error
          setFileStatuses((prev) => ({ ...prev, [fileKey]: 'error' }));
          console.error(`Failed to process ${file.name}:`, error);
        }
      }

      queryClient.invalidateQueries(['trackable-items']);
      queryClient.invalidateQueries(['trackable-files']);

      if (created === files.length) {
        faceForm.reset();
        onSuccess?.();
        onClose?.();
      } else {
        setProcessStatus(
          `Completed: ${created} of ${files.length} items created successfully`
        );
      }
    } catch (error) {
      console.error('Bulk add failed:', error);
      setProcessStatus('Error occurred during bulk add');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNonFaceSubmit = async (data: NonFaceFormData) => {
    if (!data.items) return;

    setIsProcessing(true);
    setProcessStatus('Creating items...');

    try {
      const lines = data.items.split('\n').filter((line) => line.trim());
      const labels = data.labels
        ? data.labels
            .split(',')
            .map((l) => l.trim())
            .filter(Boolean)
        : [];

      let created = 0;
      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        let name = trimmedLine;
        const attributes: Record<string, any> = {};

        if (isVehicleList) {
          // For vehicle lists, the line is the license plate
          attributes.licensePlate = trimmedLine;
        } else if (isCustomList) {
          // For custom lists, parse key=value
          const parts = trimmedLine.split('=');
          if (parts.length === 2) {
            const [key, value] = parts.map((p) => p.trim());
            name = value; // Use value as the name
            attributes[key] = value;
          } else {
            name = trimmedLine;
          }
        }

        setProcessStatus(
          `Creating item ${created + 1} of ${lines.length}: ${name}`
        );

        const newItem: CreateTrackableItem = {
          name,
          description: data.description,
          labels,
          attributes,
        };

        await createTrackableItem.mutateAsync({
          trackable_list_id: trackableList.id,
          ...newItem,
        });

        created++;
      }

      queryClient.invalidateQueries(['trackable-items']);

      nonFaceForm.reset();
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('Bulk add failed:', error);
      setProcessStatus('Error occurred during bulk add');
    } finally {
      setIsProcessing(false);
      setProcessStatus('');
    }
  };

  const onSubmit = isFaceList ? handleFaceSubmit : handleNonFaceSubmit;
  const watchedFiles = isFaceList ? faceForm.watch('files') : null;
  const selectedFiles = watchedFiles ? Array.from(watchedFiles) : [];

  return (
    <Drawer
      title={`Bulk Add ${TRACKABLE_OBJECT_TYPE_LABELS[trackableList.object_type]}s`}
      open={open}
      onClose={onClose}
    >
      <Text type="paragraph">
        Add multiple items to the "{trackableList.name}" trackable list.
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit as any)} className="mt-6">
        <div className="space-y-4">
          <Field
            label="Description"
            info="This description will be applied to all items"
          >
            <Input
              {...(form as any).register('description')}
              placeholder="Enter description (optional)..."
            />
          </Field>

          <Field
            label="Labels"
            info="Comma-separated labels to apply to all items"
          >
            <Input
              {...(form as any).register('labels')}
              placeholder="e.g., employee, security, vip"
            />
          </Field>

          {isFaceList ? (
            <Field
              label="Images"
              required
              error={faceForm.formState.errors.files?.message}
              info="Upload images of faces. Each file will create a new watchlist item with the filename as the name."
            >
              <div className="space-y-3">
                <Input
                  {...faceForm.register('files', {
                    required: 'Please select at least one image.',
                  })}
                  type="file"
                  multiple
                  accept="image/*"
                />

                {selectedFiles.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-neutral-600 dark:text-neutral-300">
                      {selectedFiles.length} file
                      {selectedFiles.length > 1 ? 's' : ''} selected:
                    </p>
                    <div className="max-h-96 space-y-1 overflow-y-auto rounded border border-neutral-200 p-2 dark:border-neutral-700">
                      {selectedFiles.map((file, index) => {
                        const fileKey = `${file.name}-${index}`;
                        const status = fileStatuses[fileKey];

                        return (
                          <div
                            key={fileKey}
                            className="flex items-center justify-between rounded bg-neutral-100 px-3 py-2 text-sm dark:bg-neutral-700"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="block truncate font-medium">
                                {file.name}
                              </span>
                              <span className="text-neutral-500 dark:text-neutral-400">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {status ? (
                                <div>
                                  {status === 'success' && (
                                    <Icon
                                      name="check-circle"
                                      className="h-5 w-5 text-green-500"
                                    />
                                  )}
                                  {status === 'error' && (
                                    <Icon
                                      name="danger"
                                      className="h-5 w-5 text-red-500"
                                    />
                                  )}
                                  {status === 'processing' && (
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                                  )}
                                  {status === 'pending' && (
                                    <div className="h-5 w-5 rounded-full border-2 border-gray-300 dark:border-gray-600" />
                                  )}
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="small"
                                  onClick={() => removeFile(index)}
                                  disabled={isProcessing}
                                >
                                  Remove
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </Field>
          ) : (
            <Field
              label={isVehicleList ? 'License Plates' : 'Items'}
              required
              error={nonFaceForm.formState.errors.items?.message}
              info={
                isVehicleList
                  ? 'Enter license plates, one per line'
                  : 'For custom items, enter key=value pairs, one per line'
              }
            >
              <Textarea
                {...nonFaceForm.register('items', {
                  required: 'Please enter at least one item.',
                })}
                placeholder={
                  isVehicleList
                    ? 'ABC123\nXYZ789\nDEF456'
                    : 'color=red\nsize=large\ntype=premium'
                }
                rows={10}
              />
            </Field>
          )}

          {/* Progress status */}
          {(isProcessing || uploadProgress.total > 0) && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {processStatus}
              </p>
              {uploadProgress.total > 0 && (
                <>
                  <p className="text-sm">
                    Uploading: {uploadProgress.current} of{' '}
                    {uploadProgress.total}
                  </p>
                  <div className="h-2 rounded-full bg-neutral-200 dark:bg-neutral-700">
                    <div
                      className="h-2 rounded-full bg-blue-600 transition-all duration-300"
                      style={{
                        width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <ButtonGroup>
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={onClose}
              disabled={isProcessing || isUploading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="small"
              loading={isProcessing || isUploading}
              disabled={
                isFaceList ? !selectedFiles.length : !nonFaceForm.watch('items')
              }
            >
              {isFaceList
                ? `Add ${selectedFiles.length || 0} Items`
                : 'Add Items'}
            </Button>
          </ButtonGroup>
        </div>
      </form>
    </Drawer>
  );
}
